const getSlotXFromPoint = (x) => Math.floor(x / 160)
const getSlotYFromPoint = (y) => Math.floor((y + 40) / 140)
const getSlotXFromId = (nodeId, svgGraph) => (nodeId === 'start' ? 0 : getSlotXFromPoint(svgGraph[nodeId].x))
const getSlotYFromId = (nodeId, svgGraph) => getSlotYFromPoint(svgGraph[nodeId].y)
const getGroupXPointFromSlot = (slot) => (slot === 0 ? 24 : 80 + 160 * slot - 30)

const buildSvgGroups = (groups) => {
  const svgGroups = {}
  if (groups) {
    const sortedGroups = JSON.parse(JSON.stringify(groups))
    sortedGroups.sort((a, b) => a.startSlotX - b.startSlotX)
    let index = 0
    for (const group of sortedGroups) {
      svgGroups[group.id] = {
        id: group.id,
        name: group.name,
        startSlotX: group.startSlotX,
        x: getGroupXPointFromSlot(group.startSlotX),
        index
      }
      index++
    }
  }

  return svgGroups
}

const createColumnsMap = (parentChildEventMap, groups, parentId, column, columns, laneHeights) => {
  for (const event of parentChildEventMap[parentId] || []) {
    let thisColumnIndex = column
    if (event.groupId && groups[event.groupId]?.startSlotX > 0) {
      thisColumnIndex = groups[event.groupId].startSlotX
    }

    let thisColumn
    if (columns[thisColumnIndex]) {
      thisColumn = columns[thisColumnIndex]
    } else {
      thisColumn = []
      columns[thisColumnIndex] = thisColumn
    }

    const laneId = event.laneId
    if (!thisColumn[laneId]) {
      thisColumn[laneId] = []
    }
    thisColumn[laneId].push(event)

    const currentCellHeight = (event.offset + 1) > (thisColumn[laneId].length - 1) ? event.offset + 1 : thisColumn[laneId].length - 1
    if (!laneHeights[laneId] || laneHeights[laneId] < currentCellHeight) {
      laneHeights[laneId] = currentCellHeight
    }

    if (parentChildEventMap[event.id]) {
      createColumnsMap(parentChildEventMap, groups, event.id, thisColumnIndex + 1, columns, laneHeights)
    }
  }
}

const buildBaseData = (events, svgGroups) => {
  const columns = []
  const laneHeights = {}
  const parentChildEventMap = { start: [] }
  for (const event of events || []) {
    const parent = event.parents[0]
    if (parent) {
      if (!parentChildEventMap[parent]) {
        parentChildEventMap[parent] = []
      }
      parentChildEventMap[parent].push(event)
    }
  }

  createColumnsMap(parentChildEventMap, svgGroups, 'start', 0, columns, laneHeights)

  for (const column of columns) {
    for (const lane in column) {
      laneHeights[lane] = Math.max(laneHeights[lane], column[lane].length)
    }
  }

  return { columns, laneHeights }
}

const buildSvgLanes = (lanes, laneHeights) => {
  const svgLanes = {}
  let laneAbove = null
  if (lanes) {
    for (const [index, lane] of lanes.entries()) {
      const height = 156 * (laneHeights[lane.id] || 1)
      svgLanes[lane.id] = {
        id: lane.id,
        x: 0,
        y: svgLanes[laneAbove] ? svgLanes[laneAbove].y + svgLanes[laneAbove].height : 0,
        height,
        name: lane.name,
        laneAbove,
        index
      }
      if (laneAbove) {
        svgLanes[laneAbove].laneBelow = lane.id
      }
      laneAbove = lane.id
    }
    if (svgLanes[laneAbove]) {
      svgLanes[laneAbove].height += 15
    }
  }

  return svgLanes
}

const buildSvgGraphNodes = (baseData, svgLanes) => {
  let events, i
  const svgGraph = {}
  for (let column = 0; column < baseData.columns.length; column++) {
    for (const role in baseData.columns[column]) {
      events = baseData.columns[column][role]

      const offsetList = []
      const detectDuplicates = new Set()
      if (events.length > 1) {
        for (i = 0; i < events.length; i++) {
          offsetList.push(i)
          if (detectDuplicates.has(events[i].offset)) {
            events[i] = JSON.parse(JSON.stringify(events[i]))
            events[i].offset = null
          } else {
            detectDuplicates.add(events[i].offset)
          }
        }
        events.sort((a, b) => { return (a.offset === null) - (b.offset === null) || +(a.offset > b.offset) || -(a.offset < b.offset) })
      }
      for (i = 0; i < (events.length); i++) {
        const offset = (events[i].offset > 0 || events[i].offset === 0) ? events[i].offset : offsetList.length === 0 ? events[i].offset : offsetList.shift()
        const x = 80 + 160 * column
        svgGraph[events[i].id] = {
          id: events[i].id,
          parents: events[i].parents,
          x,
          y: svgLanes[events[i].laneId].y + 30 + offset * 156,
          type: events[i].type,
          laneId: events[i].laneId,
          groupId: events[i].groupId,
          offset,
          childIds: svgGraph[events[i].id] ? svgGraph[events[i].id].childIds : undefined
        }
        const offsetIndex = offsetList.findIndex((o) => o === events[i].offset)
        if (offsetIndex >= 0) {
          offsetList.splice(offsetIndex, 1)
        }
        if (svgGraph[events[i].id].y < 0) {
          svgGraph[events[i].id].y = 0
        }
        for (let j = 0; j < events[i].parents.length; j++) {
          if (events[i].parents[j] !== 'start') {
            if (!svgGraph[events[i].parents[j]] || !svgGraph[events[i].parents[j]].childIds) {
              if (!svgGraph[events[i].parents[j]]) {
                svgGraph[events[i].parents[j]] = {}
              }
              svgGraph[events[i].parents[j]].childIds = []
            }
            svgGraph[events[i].parents[j]].childIds.push(events[i].id)
          }
        }
      }
    }
  }

  return svgGraph
}

export const buildSvgGraph = ({ domainEvents = [], lanes = [], groups = [] }) => {
  const svgGroups = buildSvgGroups(groups)
  const baseData = buildBaseData(domainEvents, svgGroups)
  const svgLanes = buildSvgLanes(lanes, baseData.laneHeights)
  const svgGraph = buildSvgGraphNodes(baseData, svgLanes)

  return { svgGraph, svgLanes, svgGroups }
}

const isMainParent = (svgGraph, parentId, childId) => {
  return svgGraph[childId]?.parents[0] === parentId
}

const findExtraConnections = (svgGraph) => {
  const res = []
  const tmpArr = Object.values(svgGraph || {})
  const eventsWithExtraConnections = tmpArr.filter((event) => event.parents && event.parents.length > 1)
  for (const eventWithExtraConnections of eventsWithExtraConnections) {
    for (let [index, extraConnectionParentId] of eventWithExtraConnections.parents.entries()) {
      if (index > 0) {
        if (extraConnectionParentId === 'start') {
          extraConnectionParentId = eventWithExtraConnections.id
        }

        res.push({
          parentId: extraConnectionParentId,
          childId: eventWithExtraConnections.id,
          startSlotX: getSlotXFromId(extraConnectionParentId, svgGraph) + 1,
          endSlotX: getSlotXFromId(eventWithExtraConnections.id, svgGraph) - 1,
          slotY: svgGraph[extraConnectionParentId].type === 'bpmn:Task'
            ? getSlotYFromId(extraConnectionParentId, svgGraph)
            : getSlotYFromId(eventWithExtraConnections.id, svgGraph)
        })
      }
    }
  }
  return res
}

const findLongConnections = (svgGraph) => {
  const res = []
  const nodes = Object.values(svgGraph).filter((node) => node.groupId && !node.parents.includes('start'))
  for (const node of nodes) {
    const childSlotX = getSlotXFromPoint(node.x)
    const parentSlotX = getSlotXFromPoint(svgGraph[node.parents[0]].x)
    if (childSlotX - parentSlotX > 1) {
      const parentId = node.parents[0]
      res.push({
        parentId,
        childId: node.id,
        startSlotX: parentSlotX + 1,
        endSlotX: childSlotX - 1,
        slotY: svgGraph[parentId].type === 'bpmn:Task' ? getSlotYFromPoint(svgGraph[parentId].y) : getSlotYFromPoint(svgGraph[node.id].y)
      })
    }
  }
  return res
}

const getPathsOccupyingSlot = (slotX, slotY, svgGraph) => {
  const extraConnections = findExtraConnections(svgGraph)
  const longConnections = findLongConnections(svgGraph)
  return [...extraConnections, ...longConnections].filter(
    (connection) => {
      return (connection.slotY === slotY) &&
        (connection.startSlotX <= slotX) &&
        (connection.endSlotX >= slotX)
    }
  )
}

const isNodeCrossedByPath = (node, svgGraph) => {
  const slotX = getSlotXFromPoint(node.x)
  const slotY = getSlotYFromPoint(node.y)
  let pathsOccupyingSlot = getPathsOccupyingSlot(slotX, slotY, svgGraph)
  pathsOccupyingSlot = pathsOccupyingSlot.filter((path) => path.parentId !== node.parentIds?.[0] || (svgGraph[path.parentId].type === 'bpmn:ExclusiveGateway'))
  return pathsOccupyingSlot.length > 0
}

const isNodeWithinLaneBoundaries = (node, svgLanes) => {
  return svgLanes[node.laneId]?.height >= (node.offset + 1) * 156
}

const updateNode = (node, svgGraph) => {
  if (node) {
    svgGraph[node.id] = node
  }
}

const updateNodes = (laneId, svgGraph, svgLanes) => {
  for (const node in svgGraph) {
    if (svgGraph[node].laneId === laneId) {
      const nodeCopy = Object.assign({}, svgGraph[node], { y: svgLanes[laneId].y + 30 + svgGraph[node].offset * 140 })
      updateNode(nodeCopy, svgGraph)
    }
  }
}

const updateLane = (lane, svgGraph, svgLanes) => {
  if (lane) {
    delete svgLanes[lane.id]
    svgLanes[lane.id] = lane
    if (lane.laneBelow) {
      const updatedLane = Object.assign({}, svgLanes[lane.laneBelow], { y: svgLanes[lane.id].y + svgLanes[lane.id].height })
      updateLane(updatedLane, svgGraph, svgLanes)
      updateNodes(updatedLane.id, svgGraph, svgLanes)
    }
  }
}

const moveDownNode = (node, svgGraph, svgLanes) => {
  node.offset += 1
  node.y += 156
  if (!isNodeWithinLaneBoundaries(node, svgLanes)) {
    svgLanes[node.laneId].height = (node.offset + 1) * 156
    updateLane(svgLanes[node.laneId], svgGraph, svgLanes)
  }
}

const findCollision = (svgGraph, node, laneId = null) => {
  for (const n in svgGraph) {
    if (svgGraph[n].placeholder !== true &&
      svgGraph[n].x === node.x &&
      svgGraph[n].y === node.y &&
      svgGraph[n].id !== node.id &&
      (laneId === null || node.laneId === svgGraph[n].laneId) &&
      (!isMainParent(svgGraph, node.id, svgGraph[n].id) && !isMainParent(svgGraph, svgGraph[n].id, node.id))) {
      return svgGraph[n]
    }
  }
  return null
}

const highestAndLowestNode = (svgGraph, node1, node2) => {
  const node1Parent = svgGraph[node1.parents[0]] || node1
  const node2Parent = svgGraph[node2.parents[0]] || node2
  if (node1Parent.y < node2Parent.y) {
    return { highestNode: node1, lowestNode: node2 }
  }
  if (node1Parent.y > node2Parent.y) {
    return { highestNode: node2, lowestNode: node1 }
  }
  if (node1Parent.y === node2Parent.y) {
    if (node1.y > node1Parent.y) {
      if (node1Parent.x < node2Parent.x) {
        return { highestNode: node2, lowestNode: node1 }
      } else {
        return { highestNode: node1, lowestNode: node2 }
      }
    } else {
      if (node1Parent.x < node2Parent.x) {
        return { highestNode: node1, lowestNode: node2 }
      } else {
        return { highestNode: node2, lowestNode: node1 }
      }
    }
  }
}

const updateVerticalPositionsIfCollision = (svgGraph, svgLanes, movedNodeId, forceMoveOtherShapes = false) => {
  const movedNode = svgGraph[movedNodeId]
  if (movedNode) {
    let targetNode
    if ((targetNode = findCollision(svgGraph, movedNode))) {
      if (forceMoveOtherShapes) {
        moveDownNode(targetNode, svgGraph, svgLanes)
        updateVerticalPositionsIfCollision(svgGraph, svgLanes, targetNode.id)
        updateVerticalPositionsIfCollision(svgGraph, svgLanes, movedNode.id)
      } else {
        const { highestNode, lowestNode } = highestAndLowestNode(svgGraph, movedNode, targetNode)
        moveDownNode(lowestNode, svgGraph, svgLanes)
        updateVerticalPositionsIfCollision(svgGraph, svgLanes, lowestNode.id)
        updateVerticalPositionsIfCollision(svgGraph, svgLanes, highestNode.id)
      }
    }
    if (isNodeCrossedByPath(movedNode, svgGraph)) {
      moveDownNode(movedNode, svgGraph, svgLanes)
      const allIds = Object.keys(svgGraph)
      allIds.forEach((id) => updateVerticalPositionsIfCollision(svgGraph, svgLanes, id))
    }
  }
}

const buildSettledGraph = ({ domainEvents, lanes, groups }) => {
  const { svgGraph, svgLanes } = buildSvgGraph({ domainEvents, lanes, groups })

  const allIds = Object.keys(svgGraph).sort((a, b) => {
    return (svgLanes[svgGraph[b].laneId]?.y || 0) - (svgLanes[svgGraph[a].laneId]?.y || 0)
  })
  allIds.forEach((id) => updateVerticalPositionsIfCollision(svgGraph, svgLanes, id))

  return { svgGraph, svgLanes }
}

export const computeStableOffsets = ({ domainEvents = [], lanes = [], groups = [] }) => {
  const { svgGraph } = buildSettledGraph({ domainEvents, lanes, groups })

  const offsetById = {}
  for (const id in svgGraph) {
    const node = svgGraph[id]
    if (node && node.laneId !== undefined && node.offset !== undefined) {
      offsetById[id] = node.offset
    }
  }

  return offsetById
}

// Returns each event/decision's [col, row] cell on the diagram grid: col is the column,
// row counts from the top across all lanes stacked into one axis (the next lane's rows
// continue where the previous one ended). Lane and group are reported separately.
export const computeGridPositions = ({ domainEvents = [], lanes = [], groups = [] }) => {
  const { svgGraph } = buildSettledGraph({ domainEvents, lanes, groups })

  // Rows each lane occupies after settling (max in-lane offset + 1), so lane stacking flattens onto one global row axis.
  const laneRowCount = {}
  for (const id in svgGraph) {
    const node = svgGraph[id]
    if (node && node.laneId !== undefined && node.offset !== undefined) {
      const rows = node.offset + 1
      if (!laneRowCount[node.laneId] || laneRowCount[node.laneId] < rows) {
        laneRowCount[node.laneId] = rows
      }
    }
  }

  const rowsAboveLane = {}
  let cumulativeRows = 0
  for (const lane of lanes) {
    rowsAboveLane[lane.id] = cumulativeRows
    cumulativeRows += laneRowCount[lane.id] || 1
  }

  const positionById = {}
  for (const id in svgGraph) {
    const node = svgGraph[id]
    if (node && node.x !== undefined && node.laneId !== undefined && node.offset !== undefined) {
      positionById[id] = [getSlotXFromPoint(node.x), (rowsAboveLane[node.laneId] || 0) + node.offset]
    }
  }

  return positionById
}

const resolveSlot = (eventId, slots, groupMap, eventMap, computing) => {
  if (slots.has(eventId)) {
    return slots.get(eventId)
  }

  const event = eventMap.get(eventId)
  if (!event) {
    return -1
  }

  if (computing.has(eventId)) {
    return 0
  }
  computing.add(eventId)

  if (event.groupId && groupMap.has(event.groupId)) {
    const slot = groupMap.get(event.groupId).startSlotX
    slots.set(eventId, slot)
    computing.delete(eventId)

    return slot
  }

  // Column follows the primary parent only, like the renderer (it builds columns from parents[0]).
  // Extra parents are just connection lines; counting them would push the node past a back-edge source.
  const primaryParentId = (event.parents || [])[0]
  const parentSlot = (primaryParentId && primaryParentId !== 'start')
    ? resolveSlot(primaryParentId, slots, groupMap, eventMap, computing)
    : -1

  const slot = parentSlot + 1
  slots.set(eventId, slot)
  computing.delete(eventId)

  return slot
}

export const computeEventSlots = (domainEvents, groups) => {
  const slots = new Map()
  const groupMap = new Map(groups.map((g) => [g.id, g]))
  const eventMap = new Map(domainEvents.map((e) => [e.id, e]))
  const computing = new Set()

  for (const event of domainEvents) {
    resolveSlot(event.id, slots, groupMap, eventMap, computing)
  }

  return slots
}

const resolveEventGroupId = (eventId, eventMap, ignoreId = null) => {
  const visited = new Set()
  let currentId = eventId

  while (currentId && currentId !== 'start') {
    // Bail if the walk loops back to the event being reconciled — on a cyclic graph that would
    // report the event's own group as a parent's, wrongly stripping the group's only marker.
    if (currentId === ignoreId || visited.has(currentId)) {
      break
    }
    visited.add(currentId)

    const event = eventMap.get(currentId)
    if (!event) {
      break
    }

    if (event.groupId) {
      return event.groupId
    }

    currentId = (event.parents || [])[0]
  }

  return null
}

const getEffectiveGroupIdFromParent = (event, eventMap) => {
  for (const parentId of event.parents || []) {
    if (parentId === 'start' || parentId === event.id) {
      continue
    }
    const parentEffectiveGroup = resolveEventGroupId(parentId, eventMap, event.id)
    if (parentEffectiveGroup) {
      return parentEffectiveGroup
    }
  }

  return null
}

export const reconcileEventGroupIds = (domainEvents) => {
  const eventMap = new Map(domainEvents.map((e) => [e.id, e]))
  const updates = []

  for (const event of domainEvents) {
    if (!event.groupId) {
      continue
    }

    const parentEffectiveGroup = getEffectiveGroupIdFromParent(event, eventMap)
    if (parentEffectiveGroup === event.groupId) {
      updates.push({ id: event.id, groupId: null })
    }
  }

  return updates
}

export const recalculateGroupSlots = (domainEvents, groups) => {
  if (!groups || groups.length <= 1) {
    return null
  }

  const updatedGroups = groups.map((g) => ({ ...g }))
  const sorted = [...updatedGroups].sort((a, b) => a.startSlotX - b.startSlotX)
  const eventMap = new Map(domainEvents.map((e) => [e.id, e]))
  let changed = false

  for (let i = 1; i < sorted.length; i++) {
    const slots = computeEventSlots(domainEvents, sorted)
    const laterGroupIds = new Set(sorted.slice(i).map((g) => g.id))

    let maxSlot = sorted[i - 1].startSlotX
    for (const event of domainEvents) {
      const effectiveGroupId = resolveEventGroupId(event.id, eventMap)
      if (laterGroupIds.has(effectiveGroupId)) {
        continue
      }
      const eventSlot = slots.get(event.id)
      if (eventSlot !== undefined) {
        maxSlot = Math.max(maxSlot, eventSlot)
      }
    }

    const minStartSlotX = maxSlot + 1
    if (sorted[i].startSlotX !== minStartSlotX) {
      sorted[i].startSlotX = minStartSlotX
      const groupToUpdate = updatedGroups.find((g) => g.id === sorted[i].id)
      groupToUpdate.startSlotX = minStartSlotX
      changed = true
    }
  }

  if (!changed) {
    return null
  }

  return updatedGroups
}

// ---------------------------------------------------------------------------
// Horizontal longest-path reorder engine (shared with the frontend renderer).
//
// A node's column is derived from parents[0] (its "primary parent"): the
// renderer places every event one slot to the right of parents[0]. This engine
// walks every root-to-leaf path, finds the longest path reaching each node, and
// makes that path's parent the node's parents[0] — stretching the diagram to the
// right so a multi-parent node sits past all of its parents instead of rendering
// a backward edge. The frontend runs this on every diagram refresh; the MCP runs
// it headless so a connection it adds persists the same parents[0] the UI would.
// ---------------------------------------------------------------------------

const getXPointFromSlot = (slot) => 80 + 160 * slot

const getGroupFromSlotX = (eventSlotX, svgGroups) => {
  const groups = Object.values(svgGroups || {}).sort((a, b) => a.startSlotX - b.startSlotX)
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]
    if (eventSlotX >= group.startSlotX && (!groups[i + 1] || eventSlotX < groups[i + 1].startSlotX)) {
      return group
    }
  }
}

const getGroupFromXCoordinate = (x, svgGroups) => {
  const slotX = getSlotXFromPoint(x)

  return getGroupFromSlotX(slotX, svgGroups)
}

const findGroupStartingAtCoordinate = (x, svgGroups) => {
  const slotX = getSlotXFromPoint(x)
  const groupArray = Object.values(svgGroups || {})

  return groupArray.find((g) => g.startSlotX === slotX)
}

const createAllParentChildEventMap = (svgGraph) => {
  const eventsArray = Object.values(svgGraph || {})
  eventsArray.sort((a, b) => a.id < b.id)
  const resultMap = {
    start: []
  }
  for (const event of eventsArray) {
    if (svgGraph[event.id].parents) {
      for (const parent of svgGraph[event.id].parents) {
        if (parent) {
          if (!resultMap[parent]) {
            resultMap[parent] = []
          }
          resultMap[parent].push(svgGraph[event.id])
        }
      }
    }
  }

  return resultMap
}

// moves a group (and every group to its right) one or more slots to the right
export const moveGroupRight = (group, svgGroups, shiftAmount = 1) => {
  const groupArray = Object.values(svgGroups)
  const SLOT_WIDTH = 160
  let nextGroup = groupArray.find((g) => g.startSlotX === (group.startSlotX + shiftAmount))
  if (nextGroup?.id === group.id) {
    nextGroup = null
  }
  group.startSlotX += shiftAmount
  group.x = group.x + (shiftAmount * SLOT_WIDTH)
  const previousGroup = groupArray.find((g) => g.index === (group.index - 1))
  if (previousGroup) {
    previousGroup.width += (shiftAmount * SLOT_WIDTH)
  }
  if (nextGroup) {
    moveGroupRight(nextGroup, svgGroups, shiftAmount)
  }
}

const getGroupFromParents = (eventId, svgGraph, svgGroups) => {
  if (!svgGroups || Object.keys(svgGroups).length === 0) {
    return undefined
  }
  const event = svgGraph[eventId]
  if (!event) {
    return undefined
  }
  if (event.groupId) {
    return svgGroups[event.groupId]
  }
  if (event.parents && event.parents[0] === 'start') {
    return getGroupFromSlotX(0, svgGroups)
  }

  return getGroupFromParents(event.parents[0], svgGraph, svgGroups)
}

export const getGroupFromNode = (node, svgGroups, svgGraph) => {
  if (!svgGroups || Object.keys(svgGroups).length === 0) {
    return undefined
  }
  if (node.groupId && svgGroups[node.groupId]) {
    return svgGroups[node.groupId]
  }
  const groupFromX = getGroupFromXCoordinate(node.x, svgGroups)
  if (groupFromX) {
    return groupFromX
  }
  const groupFromParents = getGroupFromParents(node.id, svgGraph, svgGroups)
  if (groupFromParents) {
    return groupFromParents
  }

  return undefined
}

// returns true if the new path anchors the child closer (further right) than the existing one
const selectNewPath = (newPath, existingPath, svgGraph, child) => {
  const newPathParentId = newPath[0].parent
  const newPathParent = svgGraph[newPathParentId]
  const existingPathParentId = existingPath[0].parent
  if (existingPathParentId === 'start') {
    return false
  }
  const existingPathParent = svgGraph[existingPathParentId]
  const childNode = svgGraph[child.id]
  return newPathParent?.x < childNode.x && newPathParent.x > existingPathParent.x
}

// walks the graph collecting every path; a path breaks whenever a group boundary is crossed
export const findPaths = (svgGraph, svgGroups, aggregatedPaths, parentChildMap = null, currentNode = 'start', path = null, pathLengths = {}, currentPathNodes = null, pathStartMap = null) => {
  if (parentChildMap === null) {
    parentChildMap = createAllParentChildEventMap(svgGraph)
  }
  if (currentPathNodes === null) {
    currentPathNodes = new Set()
  }
  if (pathStartMap === null) {
    pathStartMap = new Map()
  }
  const currentPath = path || []
  if (!parentChildMap[currentNode]) {
    if (currentPath.length > 0) {
      aggregatedPaths.push(currentPath)
    }
  } else {
    for (const child of parentChildMap[currentNode]) {
      const childGroup = getGroupFromNode(child, svgGroups, svgGraph)
      let parentGroup
      if (currentNode === 'start') {
        parentGroup = getGroupFromNode(child, svgGroups, svgGraph)
      } else {
        parentGroup = getGroupFromNode(svgGraph[currentNode], svgGroups, svgGraph)
      }
      if (parentGroup !== childGroup) {
        // every time we hit a group boundary we break up the path
        if (currentPath.length > 0) {
          aggregatedPaths.push(currentPath)
          if (currentPath[0]?.child) {
            const pathStartChild = currentPath[0].child
            if (!pathStartMap.has(pathStartChild)) {
              pathStartMap.set(pathStartChild, currentPath)
            }
          }
        }
        if (!child.groupId) {
          continue
        }
        if (parentGroup.startSlotX > childGroup.startSlotX) {
          continue
        }
        const newPath = [{ child: child.id, parent: currentNode, x: child.x, groupStartSlotX: childGroup?.startSlotX }]
        let shouldSelectNewPath = true
        const existingPath = pathStartMap.get(child.id)
        if (existingPath !== undefined) {
          shouldSelectNewPath = selectNewPath(newPath, existingPath, svgGraph, child)
        }
        if (shouldSelectNewPath) {
          pathStartMap.set(child.id, newPath)
          findPaths(svgGraph, svgGroups, aggregatedPaths, parentChildMap, child.id, newPath, pathLengths, new Set(), pathStartMap)
        }
      } else {
        const currentPathLength = path?.length || 0
        // Work out up front whether this child continues the path or ends it.
        const loopbackToPreviousGroup = parentGroup?.startSlotX > childGroup?.startSlotX
        const loopbackToStart = currentPath.length > 1 && (child.parents[0] === 'start')
        const circularPath = currentPathNodes.has(child.id)
        const childIsAGroupStartNode = (typeof child?.groupId === 'string' && child?.groupId !== '')
        const loopBackToGroupStart = childIsAGroupStartNode && currentPath.length > 1 && childGroup?.id === parentGroup?.id
        if (!loopbackToStart && !loopbackToPreviousGroup && !circularPath && !loopBackToGroupStart) {
          // OPTIMIZATION: Better early termination with pathLengths.
          // We only record the length for children the path actually continues through.
          // A loop-back / circular dead-end must NOT update pathLengths — doing so would
          // inflate the recorded length and prune a legitimate longer forward path that
          // reaches this child later, leaving a multi-parent event anchored to its shorter
          // parent and rendered as a backward edge.
          if (pathLengths[child.id] !== undefined) {
            if (currentPathLength <= pathLengths[child.id]) {
              if (currentPath.length > 0) {
                aggregatedPaths.push(currentPath)
                continue
              }
            } else {
              pathLengths[child.id] = currentPathLength
            }
          } else {
            pathLengths[child.id] = currentPathLength
          }
          if (parentChildMap[currentNode].length === 1) {
            currentPath.push({ child: child.id, parent: currentNode, x: child.x, groupStartSlotX: childGroup?.startSlotX })
            currentPathNodes.add(currentNode)
            findPaths(svgGraph, svgGroups, aggregatedPaths, parentChildMap, child.id, currentPath, pathLengths, currentPathNodes, pathStartMap)
            currentPathNodes.delete(currentNode)
          } else {
            const newPathNodes = new Set(currentPathNodes)
            newPathNodes.add(currentNode)
            findPaths(svgGraph, svgGroups, aggregatedPaths, parentChildMap, child.id, [...currentPath, { child: child.id, parent: currentNode, x: child.x, groupStartSlotX: childGroup?.startSlotX }], pathLengths, newPathNodes, pathStartMap)
          }
        } else {
          if (currentPath.length > 0) {
            aggregatedPaths.push(currentPath)
          }
        }
      }
    }
  }
}

// collects all paths and sorts them: first by group left-to-right, then by length,
// so the longest paths are laid out first when reordering
const getLongestPaths = (svgGraph, svgGroups) => {
  const aggregatedPaths = []
  findPaths(svgGraph, svgGroups, aggregatedPaths)
  aggregatedPaths.sort((arr1, arr2) => {
    if (arr2.length === arr1.length && arr2[0].groupStartSlotX === arr1[0].groupStartSlotX) {
      // same length and group — sort on a stable key so order doesn't change randomly
      const strArr1 = JSON.stringify(arr1.map((el) => el.parent))
      const strArr2 = JSON.stringify(arr2.map((el) => el.parent))
      if (strArr1 < strArr2) {
        return -1
      } else {
        return 1
      }
    }
    if (arr2[0].groupStartSlotX === arr1[0].groupStartSlotX) {
      return arr2.length - arr1.length
    }

    return arr1[0].groupStartSlotX - arr2[0].groupStartSlotX
  })

  return aggregatedPaths
}

const getEventXBasedOnParentAndGroup = (eventId, parentId, svgGraph, svgGroups) => {
  if (svgGraph[eventId].groupId) {
    if (!svgGroups) {
      throw new Error('svgGroups is required')
    }
    const group = svgGroups[svgGraph[eventId].groupId]

    return getXPointFromSlot(group.startSlotX)
  } else if (parentId === 'start') {
    return 80
  } else {
    return svgGraph[parentId].x + 160
  }
}

// reorders each event's parents[0] to its longest-path parent and recomputes x,
// stretching the diagram to the right. Mutates svgGraph (and svgGroups when a
// moved event pushes a group right). Returns the ids of events whose parents changed.
export const reorderEventsToLongestPath = (svgGraph, svgGroups) => {
  const movedNodeIds = new Set()
  const alreadyWalked = {}
  const longestPaths = getLongestPaths(svgGraph, svgGroups)
  for (const longestPath of longestPaths) {
    for (const step of longestPath) {
      const currentEvent = svgGraph[step.child]
      if (!alreadyWalked[currentEvent.id]) {
        if (currentEvent.parents[0] !== step.parent) {
          const group = getGroupFromXCoordinate(currentEvent.x, svgGroups)
          const parentGroup = getGroupFromXCoordinate(svgGraph[step.parent]?.x, svgGroups)
          if (group?.id !== parentGroup?.id) {
            if (parentGroup?.startSlotX < group?.startSlotX) {
              currentEvent.parents.splice(currentEvent.parents.indexOf(step.parent), 1)
              currentEvent.parents.unshift(step.parent)
              movedNodeIds.add(currentEvent.id)
            }
            // else: backward connection across groups — leave parents[0] as is
          } else {
            currentEvent.parents.splice(currentEvent.parents.indexOf(step.parent), 1)
            currentEvent.parents.unshift(step.parent)
            movedNodeIds.add(currentEvent.id)
          }
        }
        const newEventX = getEventXBasedOnParentAndGroup(currentEvent.id, step.parent, svgGraph, svgGroups)
        const oldX = currentEvent.x
        if (oldX !== newEventX) {
          currentEvent.x = newEventX
          if (newEventX > oldX) {
            const group = findGroupStartingAtCoordinate(newEventX, svgGroups)
            if (group && group.startSlotX !== 0 && !currentEvent.groupId) {
              moveGroupRight(group, svgGroups)
            }
          }
        }
        alreadyWalked[currentEvent.id] = true
      }
    }
  }

  return movedNodeIds
}
